import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n/index.jsx';
import { pickLang } from '../content.jsx';
import { flagUrl, countryName } from '../team.js';
import BannerDecor from './BannerDecor.jsx';
import './mediaBanner.css';

// Page banner: photo, video or GIF (reusable on any page).
//
// Size (Admin > banner):
// - width: "full" (edge to edge, up to 2560 px then centred), "site" (the
//   page content width, rounded corners) or a % of the screen;
// - height: "auto" (the media's own shape, nothing cut) or a % of the
//   screen height between a min and max in px; phones (≤ 640 px) can have
//   their own height (default: auto);
// - fit: "contain" (show everything; side bars in the chosen colour) or
//   "cover" (fill the box, cropped around the focal point).
// The poster and the video always share the same box, fit and focus.
// Banners saved without these settings get the defaults (full width, auto
// height, show all).
//
// Playback: the poster shows at once; the video loads once on screen
// (preload="metadata") and plays muted / inline without controls (iPhone
// too). Loop off: plays once and stays on the last frame, with "Replay".
// Reduced motion: poster only — or, with loop off, the last frame.
// Save-Data / slow connection: poster only. Pause / play button always.

// { type, image, video, poster, loop, speed, darken, text, fit, ratio, focalX, focalY, content, look,
//   width, widthPct, height, heightVh, minPx, maxPx, phoneHeight, phoneVh, barColor }
// — older pages only had `banner` (a photo).
export function bannerOf(doc) {
  const m = doc?.bannerMedia;
  if (m && (m.type !== 'photo' || m.image)) return m;
  return { ...(m || {}), type: 'photo', image: doc?.banner || '', video: '', poster: '' };
}
export const hasBanner = (m) => !!(m && (m.type === 'photo' ? m.image : m.video || m.poster));

// Settings with their defaults (Full width + Auto height + Show all).
export function resolveBanner(m = {}) {
  return {
    width: m.width || 'full', widthPct: m.widthPct ?? 100,
    height: m.height || 'auto', heightVh: m.heightVh ?? 50, minPx: m.minPx ?? 180, maxPx: Math.max(m.maxPx ?? 900, m.minPx ?? 180),
    phoneHeight: m.phoneHeight || 'auto', phoneVh: m.phoneVh ?? 35,
    fit: m.fit || 'contain', barColor: m.barColor || '',
    focalX: m.focalX ?? 50, focalY: m.focalY ?? 50,
  };
}
export const fitOf = (m) => resolveBanner(m).fit;

// Light and effects. Banners saved before these settings keep their look
// (darkening only, no effects).
export const ELEGANT = { brightness: 100, overlay: 'gradient', overlayOpacity: 55, overlayColor: '#1c2a44', tint: 0, kenBurns: 'slow', entrance: true, sweep: true, decor: 'none', edge: 'curve', arrow: false };
export const NO_EFFECTS = { ...ELEGANT, kenBurns: 'off', entrance: false, sweep: false, decor: 'none', edge: 'straight', arrow: false };
export const CREAM = '#faf6ef';
export const NAVY_OVERLAY = '#1c2a44';
export function lookOf(m = {}) {
  if (m.look) return { ...ELEGANT, ...m.look };
  const d = Number(m.darken) || 0;
  return { ...NO_EFFECTS, overlay: d > 0 ? 'dark' : 'none', overlayOpacity: d, overlayColor: '#000000' };
}
// Content on the banner (eyebrow, title, subtitle, buttons, badges, logo).
const biSet = (v) => !!(v && (v.fr || v.en));
export function hasContent(c) {
  return !!(c && (biSet(c.eyebrow) || biSet(c.title) || biSet(c.subtitle) || c.logo || c.buttons?.length
    || (c.badges?.mode === 'flags' && c.badges.countries?.length) || (c.badges?.mode === 'text' && biSet(c.badges.text))));
}
export const TEXT_NAVY = '#0f2a4a';
// White text, except on a light overlay.
export const textColorOf = (c, look) => (c?.color === 'white' || c?.color === 'navy' ? c.color : look.overlay === 'light' ? 'navy' : 'white');

const rgba = (hex, a) => { const n = parseInt(String(hex).slice(1), 16) || 0; return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${Math.max(0, Math.min(1, a)).toFixed(3)})`; };
// Overlay backgrounds: { desk, phone }. The gradient only covers the text
// side (faces on the other side stay bright); phones get a stronger,
// even overlay behind centred text.
export function overlayLayers(look, content) {
  const a = (look.overlayOpacity || 0) / 100;
  const c = look.overlay === 'light' ? (look.overlayColor || CREAM) : (look.overlayColor || NAVY_OVERLAY);
  const text = hasContent(content);
  let desk = null;
  if (look.overlay === 'dark' || look.overlay === 'light') desk = rgba(c, a);
  else if (look.overlay === 'gradient' && a > 0) {
    const x = content?.posX || 'left';
    const y = content?.posY || 'middle';
    if (x === 'left' || x === 'right') desk = `linear-gradient(${x === 'left' ? 90 : 270}deg, ${rgba(c, a)} 0%, ${rgba(c, a * 0.85)} 36%, ${rgba(c, a * 0.35)} 56%, ${rgba(c, 0)} 72%)`;
    else if (y === 'middle') desk = `radial-gradient(ellipse 62% 75% at 50% 50%, ${rgba(c, a)} 0%, ${rgba(c, a * 0.55)} 50%, ${rgba(c, 0)} 80%)`;
    else desk = `linear-gradient(${y === 'top' ? 180 : 0}deg, ${rgba(c, a)} 0%, ${rgba(c, a * 0.6)} 40%, ${rgba(c, 0)} 72%)`;
  }
  let phone = desk;
  if (text && look.overlay !== 'none') {
    const p = Math.min(0.88, Math.max(a, 0.3) + 0.15);
    phone = `linear-gradient(180deg, ${rgba(c, p * 0.75)} 0%, ${rgba(c, p)} 100%)`;
  } else if (text) phone = `linear-gradient(180deg, ${rgba('#000000', 0.25)} 0%, ${rgba('#000000', 0.45)} 100%)`;
  return { desk, phone };
}
const scrollBehavior = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; } catch { return 'auto'; } };

// The text block on the banner.
function BannerContent({ c, look, lang, titleTag, preview }) {
  const { t } = useI18n();
  const H = titleTag;
  const eyebrow = pickLang(c.eyebrow, lang);
  const title = pickLang(c.title, lang);
  const subtitle = pickLang(c.subtitle, lang);
  const badges = c.badges?.mode === 'flags' ? (c.badges.countries || []).map((code) => ({ code, label: countryName(code, lang) }))
    : c.badges?.mode === 'text' ? pickLang(c.badges.text, lang).split(/\s*[·•,|]\s*/).filter(Boolean).map((label) => ({ label })) : [];
  const color = textColorOf(c, look);
  let i = 0;
  const d = () => ({ '--d': i++ });
  const go = (href) => (e) => {
    if (preview) { e.preventDefault(); return; }
    if (href.startsWith('#')) {
      const el = document.getElementById(href.slice(1));
      if (el) { e.preventDefault(); el.scrollIntoView({ behavior: scrollBehavior(), block: 'start' }); }
    }
  };
  const cls = ['mb-content', `x-${c.posX || 'left'}`, `y-${c.posY || 'middle'}`, `is-${color}`, c.glass ? 'is-glass' : '', look.entrance ? 'fx-in' : '', look.sweep ? 'fx-sweep' : ''].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <div className="mb-inner">
        {c.logo && <img className="mb-logo" src={c.logo} alt="" style={d()} />}
        {eyebrow && <p className="mb-eyebrow" style={d()}>{eyebrow}</p>}
        {title && <H className="mb-title" style={d()}><span>{title}</span></H>}
        {subtitle && <p className="mb-subtitle" style={d()}>{subtitle}</p>}
        {c.buttons?.length > 0 && (
          <div className="mb-buttons" style={d()}>
            {c.buttons.map((b, k) => {
              const label = pickLang(b.label, lang);
              if (!label || !b.href) return null;
              const cl = `mb-btn is-${b.style === 'outline' ? 'outline' : 'gold'}`;
              if (b.href.startsWith('/')) return <Link key={k} className={cl} to={b.href} onClick={go(b.href)}>{label}</Link>;
              const ext = b.href.startsWith('https://');
              return <a key={k} className={cl} href={b.href} onClick={go(b.href)} {...(ext ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{label}</a>;
            })}
          </div>
        )}
        {badges.length > 0 && (
          <ul className="mb-badges" style={d()} aria-label={t('banner.badges')}>
            {badges.map((b) => <li key={b.code || b.label}>{b.code && flagUrl(b.code) && <img src={flagUrl(b.code)} alt="" width="18" height="13" />}{b.label}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}
const PHONE_MAX = 640;

// Box size for a screen { w, h }: width style + height (px) or aspect ratio.
// `text`: content on the banner — the box keeps room for it.
export function bannerBox(m, ratio, screen, text = false) {
  const s = resolveBanner(m);
  const phone = screen.w <= PHONE_MAX;
  const pad = screen.w >= 768 ? 24 : 16;
  const style = { marginLeft: 'auto', marginRight: 'auto' };
  if (s.width === 'site') Object.assign(style, { width: `calc(100% - ${2 * pad}px)`, maxWidth: 1160 - 2 * pad });
  else if (s.width === 'custom' && s.widthPct < 100) style.width = `${s.widthPct}%`;
  else Object.assign(style, { width: '100%', maxWidth: 2560 });
  const framed = s.width === 'site' || (s.width === 'custom' && s.widthPct < 100);
  if (framed) Object.assign(style, { borderRadius: phone ? 12 : 16, marginTop: phone ? 12 : 20 });
  let mode = phone ? ({ auto: 'auto', same: s.height, custom: 'custom' }[s.phoneHeight] || 'auto') : s.height;
  if (mode === 'custom') {
    const vh = phone && s.phoneHeight === 'custom' ? s.phoneVh : s.heightVh;
    style.height = Math.round(Math.min(s.maxPx, Math.max(s.minPx, (vh / 100) * screen.h)));
  } else style.aspectRatio = String(ratio || 16 / 7);
  if (text) style.minHeight = phone ? 480 : screen.w < 1024 ? 440 : 520;
  return { style, framed, fit: s.fit, s };
}

// The real screen, updated when its width changes (not on the phone's
// address bar showing / hiding, so the banner never jumps while scrolling).
function useScreen(fixed) {
  const get = () => ({ w: document.documentElement.clientWidth || window.innerWidth, h: document.documentElement.clientHeight || window.innerHeight });
  const [scr, setScr] = useState(() => fixed || get());
  useEffect(() => {
    if (fixed) { setScr(fixed); return undefined; }
    const on = () => setScr((old) => { const n = get(); return n.w === old.w ? old : n; });
    window.addEventListener('resize', on);
    window.addEventListener('orientationchange', on);
    return () => { window.removeEventListener('resize', on); window.removeEventListener('orientationchange', on); };
  }, [fixed?.w, fixed?.h]);
  return scr;
}

function conditions() {
  let reduce = false;
  let lowData = false;
  try {
    reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const c = navigator.connection;
    lowData = !!(c && (c.saveData || ['slow-2g', '2g'].includes(c.effectiveType)));
  } catch { /* old browser */ }
  return { reduce, lowData, motion: !reduce && !lowData };
}

// First frame of a GIF, for "paused" / reduced motion when there's no poster.
function GifStill({ src }) {
  const ref = useRef(null);
  useEffect(() => {
    const img = new Image();
    let alive = true;
    img.onload = () => {
      const c = ref.current;
      if (!alive || !c) return;
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      c.getContext('2d').drawImage(img, 0, 0);
    };
    img.src = src;
    return () => { alive = false; };
  }, [src]);
  return <canvas ref={ref} className="media-banner-fill" aria-hidden="true" />;
}

export default function MediaBanner({ media, className = '', children, label, screen, onPick, titleTag = 'h2' }) {
  const { t, lang } = useI18n();
  const outer = useRef(null);
  const box = useRef(null);
  const video = useRef(null);
  const [env] = useState(conditions);
  const m = media || {};
  const loop = m.loop === true;
  // Reduced motion with loop off: freeze on the real last frame.
  const freezeAtEnd = env.reduce && !env.lowData && !loop;
  const [inView, setInView] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(!env.motion);
  const [ended, setEnded] = useState(false);
  const [atEnd, setAtEnd] = useState(false); // last frame shown (frozen)
  const [measured, setMeasured] = useState(null); // ratio when not saved yet
  const isGif = m.type === 'gif' && /\.gif$/i.test(m.video || '');
  const isVideo = (m.type === 'video' || m.type === 'gif') && m.video && !isGif;
  const ratio = m.ratio || measured;
  const scr = useScreen(screen);
  const content = hasContent(m.content) ? m.content : null;
  const look = lookOf(m);
  const { style: boxStyle, framed, fit, s: set } = bannerBox(m, ratio, scr, !!content);
  const layers = overlayLayers(look, content);
  const kb = m.type === 'photo' && look.kenBurns !== 'off' ? ` mb-kb-${look.kenBurns}` : '';
  const toNext = () => {
    if (onPick || !outer.current) return;
    const top = outer.current.getBoundingClientRect().bottom + window.scrollY - (document.querySelector('.site-header')?.offsetHeight || 0);
    window.scrollTo({ top, behavior: scrollBehavior() });
  };

  useEffect(() => { setEnded(false); setAtEnd(false); setPlaying(false); setMeasured(null); }, [m.video]);

  // Load the video only once the banner is (nearly) on screen.
  useEffect(() => {
    if (!isVideo || !box.current) return undefined;
    if (!('IntersectionObserver' in window)) { setInView(true); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(box.current);
    return () => io.disconnect();
  }, [isVideo]);

  // iPhone autoplay needs the muted / playsinline attributes before the source.
  useEffect(() => {
    const v = video.current;
    if (!v || !isVideo || !inView) return;
    v.muted = true; v.defaultMuted = true;
    v.setAttribute('muted', ''); v.setAttribute('playsinline', ''); v.setAttribute('webkit-playsinline', '');
    v.playbackRate = m.speed || 1;
    if (!paused && !ended) v.play().catch(() => setPaused(true));
    else if (paused) v.pause();
  }, [isVideo, inView, paused, ended, m.speed, m.video]);

  const onMeta = (e) => {
    const v = e.currentTarget;
    v.playbackRate = m.speed || 1;
    if (!m.ratio && v.videoWidth && v.videoHeight) setMeasured(v.videoWidth / v.videoHeight);
    if (freezeAtEnd && paused && Number.isFinite(v.duration)) v.currentTime = Math.max(0, v.duration - 0.05);
  };
  const toggle = () => {
    const v = video.current;
    if (ended || atEnd) {
      // "Replay": from the start, once (or looping if loop is on).
      if (v) { v.currentTime = 0; }
      setEnded(false); setAtEnd(false); setPaused(false);
      return;
    }
    setPaused((p) => !p);
  };

  // Without a poster, or to freeze on the last frame, the video itself is loaded.
  const showVideoSrc = isVideo && inView && (env.motion || !paused || !m.poster || freezeAtEnd);
  const videoVisible = playing || ended || atEnd || (!m.poster && !freezeAtEnd);
  const replay = ended || atEnd;
  const btnLabel = replay ? t('banner.replay') : paused ? t('banner.play') : t('banner.pause');
  const style = {
    ...boxStyle,
    '--fx': `${set.focalX}%`, '--fy': `${set.focalY}%`, '--fit': fit,
    '--mb-bright': look.brightness / 100,
    background: fit === 'contain' ? (set.barColor || 'transparent') : undefined,
  };
  // Admin: click the preview to choose the focal point.
  const pick = onPick ? (e) => {
    if (e.target.closest('button')) return;
    const r = e.currentTarget.getBoundingClientRect();
    onPick(Math.round(((e.clientX - r.left) / r.width) * 100), Math.round(((e.clientY - r.top) / r.height) * 100));
  } : undefined;

  return (
    <div ref={outer} className={`media-banner-outer is-${fit}${framed ? ' is-framed' : ''} ${className}`}>
      <div ref={box} className={`media-banner${onPick ? ' is-pickable' : ''}${kb}${look.brightness !== 100 ? ' is-filtered' : ''}`} style={style} role={label ? 'img' : undefined} aria-label={label || undefined} onClick={pick}>
        {m.type === 'photo' && m.image && (
          <img className="media-banner-fill" src={m.image} alt="" fetchpriority="high"
            onLoad={(e) => { if (!m.ratio && e.currentTarget.naturalHeight) setMeasured(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight); }} />
        )}
        {(isVideo || isGif) && m.poster && (
          <img className="media-banner-fill" src={m.poster} alt="" fetchpriority="high"
            onLoad={(e) => { if (!m.ratio && !measured && e.currentTarget.naturalHeight) setMeasured(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight); }} />
        )}
        {isVideo && (
          <video ref={video} className={`media-banner-fill media-banner-video${videoVisible ? ' is-playing' : ''}`}
            src={showVideoSrc ? m.video : undefined} poster={m.poster || undefined} preload="metadata"
            muted playsInline loop={loop} autoPlay={env.motion && !paused} aria-hidden="true" tabIndex={-1}
            onPlaying={() => { setPlaying(true); setEnded(false); setAtEnd(false); }}
            onEnded={() => { if (!loop) setEnded(true); }}
            onSeeked={(e) => { if (freezeAtEnd && paused && e.currentTarget.currentTime > 0) setAtEnd(true); }}
            onLoadedMetadata={onMeta} />
        )}
        {isGif && (paused
          ? (m.poster ? null : <GifStill src={m.video} />)
          : <img className="media-banner-fill" src={m.video} alt=""
            onLoad={(e) => { if (!m.ratio && !measured && e.currentTarget.naturalHeight) setMeasured(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight); }} />)}
        {look.tint > 0 && <span className="mb-tint" style={{ opacity: Math.min(0.6, (look.tint / 100) * 2) }} aria-hidden="true" />}
        {(layers.desk || layers.phone) && <span className="media-banner-shade mb-shade-desk" style={{ background: layers.desk || 'none' }} aria-hidden="true" />}
        {(layers.desk || layers.phone) && <span className="media-banner-shade mb-shade-phone" style={{ background: layers.phone || 'none' }} aria-hidden="true" />}
        {look.decor !== 'none' && <BannerDecor kind={look.decor} side={content?.posX === 'right' ? 'left' : 'right'} />}
        {content && <BannerContent c={content} look={look} lang={lang} titleTag={titleTag} preview={!!onPick} />}
        {!content && children && <div className="media-banner-content">{children}</div>}
        {look.edge !== 'straight' && !framed && (
          <svg className="mb-edge" viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true" focusable="false">
            <path d={look.edge === 'curve' ? 'M0 0 Q50 13 100 0 L100 10 L0 10 Z' : 'M0 10 L100 0 L100 10 Z'} />
          </svg>
        )}
        {look.arrow && (
          <button type="button" className="mb-arrow" onClick={toNext} aria-label={t('banner.scrollDown')} title={t('banner.scrollDown')}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d="M6 9l6 6 6-6" /></svg>
          </button>
        )}
        {(isVideo || isGif) && (
          <button type="button" className="media-banner-toggle" onClick={toggle} aria-pressed={!paused && !replay}
            aria-label={btnLabel} title={btnLabel}>
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              {replay
                ? <path d="M12 5V2L7 6l5 4V7a5 5 0 11-5 5H5a7 7 0 107-7z" fill="currentColor" />
                : paused ? <path d="M7 4l13 8-13 8z" fill="currentColor" /> : <path d="M6 4h4v16H6zM14 4h4v16h-4z" fill="currentColor" />}
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
